import ProjectInUploadResp from "@/interface/api/ProjectInUploadResp";
import PublicPageInUploadResp from "@/interface/api/PublicPageInUploadResp";
import PublicPageResp from "./interface/api/PublicPageResp";
import BadgeProjectResp, { RespBadge } from "./interface/api/BadgeProjectResp";
import UserResp from "./interface/api/UserResp";
import AccountResp from "./interface/api/AccountResp";
import ProfileResp from "./interface/api/ProfileResp";

import constVariable from "@/const";
import common from "./common";

export class URLs {
    getUploadFileURL(project?: ProjectInUploadResp) {
        return this.getUploadFileURLFromId(project?.id)
    }
    getUploadFileURLFromId(projectId?: number) {
        return "/upload/file/" + (projectId === undefined ? "" : projectId + "/")
    }

    getUploadPublicPageURL(project?: ProjectInUploadResp, publicPage?: PublicPageInUploadResp) {
        return this.getUploadPublicPageURLFromId(project?.id, publicPage?.id);
    }

    getUploadPublicPageURLFromId(projectId?: number, pageId?: number) {
        return "/upload/public-page/" + (projectId === undefined ? "" : projectId + "/" + (pageId === undefined ? "": pageId + "/"))
    }

    getUploadSettingURL(project?: ProjectInUploadResp) {
        return this.getUploadSettingURLFromId(project?.id)
    }
    getUploadSettingURLFromId(projectId?: number) {
        return "/upload/setting/" + (projectId === undefined ? "" : projectId + "/")
    }

    getThumbnailURL(userId:number, publicPage: PublicPageResp | PublicPageInUploadResp) {
        return this.getThumbnailURLFrom(userId, publicPage.baseProjectId, publicPage.id, publicPage.thumbnail);
    }

    getThumbnailURLFrom(userId:number, projectId:number, publicPageId:number, thumbnail:string) {
        if (thumbnail) {
            const DEFAULT = "[_default_]";
            if(thumbnail.startsWith(DEFAULT)){
                return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/public-page/thumbnail/default/" + thumbnail.substring(DEFAULT.length)
            }else{
                return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/public-page/thumbnail/" + userId + "/" + projectId + "/" + publicPageId + "/" + thumbnail
            }
        } else {
            return null
        }
    }

    getDefaultThumbnailForUpload(imageNumber: string | number): string {
        return `[_default_]${imageNumber}.jpg`
    }

    getDefaultImageNumber(imageSrc?: string): number | undefined {
        if(imageSrc){
            const defaultImage = /\[_default_\](\d+).jpg/

            const resultArr = defaultImage.exec(imageSrc)
            if (resultArr?.length) {
                return Number(resultArr[1])
            } 
        }
        return undefined
    }

    getUserImageSrc(user: UserResp | AccountResp) {
        if (user && user.image) {
            const DEFAULT = "[_default_]";
            if(user.image.startsWith(DEFAULT)){
                return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/user/image/default/" + user.image.substring(DEFAULT.length);
            }else{
                return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/user/image/" + user.id + "/" + user.image;
            }
        } else {
            return null
        }
    }

    getUserBackgroundSrc(profile: ProfileResp) {
        if (profile && profile.backgroundImage) {
            return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/user/background/" + profile.userId + "/" + profile.backgroundImage
        } else {
            return null
        }
    }

    getBadgeImageURL(project: BadgeProjectResp, badge: RespBadge) {
        if (badge.imageURL) {
            return badge.imageURL;
        } else {
            const viewCount = badge.publicPage?.viewCount || 0;
            const raito = badge.totalGotUser / (badge.totalGotUser + viewCount) * 100
            let raitoStr = "1";
            if (raito < 5) {
                raitoStr = "3"
            } else if (raito < 30) {
                raitoStr = "2"
            }

            let viewStr = "1";
            if (viewCount > 100000) {
                viewStr = "3";
            } else if (viewCount > 1000) {
                viewStr = "2";
            }

            return process.env.VUE_APP_MELON + process.env.VUE_APP_CDN + "/badge/badge_" + viewStr + raitoStr + ".jpg"
        }
    }

    getUserURL(user: UserResp) {
        return '/' + user.userName;
    }

    getPublicPageURL(publicPage: PublicPageResp) {
        return this.getPublicPageURLFrom(publicPage.user.userName, publicPage.titleURL);
    }

    getPublicPageURLFrom(userName: string, titleURL: string) {
        return '/' + (userName ? userName : "shared-space") + '/' + titleURL;
    }

    //kiwi側投稿URL
    getKiwiUploadURL(isLogin: boolean, projectId: number | null | undefined, sharedKey: string | undefined) {
        return isLogin ? 
            this.getKiwiLoginUploadURL(projectId) :
            this.getKiwiSharedUploadURL(sharedKey);
    }

    getKiwiLoginUploadURL(projectId: number | null | undefined){
        return (process.env.VUE_APP_KIWI_UPLOAD + "/upload/" + (projectId ? projectId : ""))
            + "?" + constVariable.KIWI_UPLOAD_FROM_IFRAME + "=1";
    }
    getKiwiSharedUploadURL(sharedKey: string | undefined){
        return this.getKiwiSharedUploadURLWithoutIframe(sharedKey)
            + "?" + constVariable.KIWI_UPLOAD_FROM_IFRAME + "=1";
    }
    getKiwiSharedUploadURLWithoutIframe(sharedKey: string | undefined){
        return (process.env.VUE_APP_KIWI_UPLOAD + "/shared-upload/" + sharedKey);
    }

    
    getRandomSharedSpaceTitle(){
        const KEY = "shared-space-name";
        let value = localStorage.getItem(KEY);
        if(!value){
            value = common.getRandomString(30);
            localStorage.setItem(KEY, value);
        }
        return value;
    }

    //kiwi側表示URL
    getKiwiViewURL(
        userName: string,
        projectId: number,
        htmlPath: string,
        addLoginSupportQuery =false,
        addTimeQuery = false,
        detectSizeChanged = true
    ) {
        //kiwiViewを表示する際、末尾に「/」を付与する（末尾に「/」がない場合、kiwi側で無駄にリダイレクトが走るため。「/」有無で相対パスの位置がずれるため）
        const ret = process.env.VUE_APP_KIWI_VIEW + "/" + userName + "/" + projectId + "/" + htmlPath;

        const params = new URLSearchParams(location.search);

        if (addLoginSupportQuery) {
            params.append("enable-message-login-request", "true");
        }
        if (addTimeQuery) {
            params.append("t", (new Date()).getTime().toString());
        }
        if (detectSizeChanged) {
            params.append("enable-message-size-changed", "true");
        }
        return ret + "?" + params.toString();
    }

    getIconURL(iconName: string) {
        return require(`@/assets/icons.svg`) + "#" + iconName
    }



    isEqualURL(url1:string, url2:string){
        if (!url1 || !url2) {
            return false;
        }
    
        const normalizeURL = (url: string) => url.endsWith("/") ? url.slice(0, -1) : url;
    
        return normalizeURL(url1) === normalizeURL(url2);
    }
}


export default new URLs();